import React, { useState } from "react";

import { AlertTriggerDTO, TRIGGER_TYPE_OPTIONS } from "api/model/alerts/alert-types";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { T } from "services/lang/LanguageService";
import EditTriggerForm from "./EditTriggerForm";
import NewTriggerForm from "./NewTriggerForm";
import TriggerCard from "./TriggerCard";
import { TriggerData, TriggerFormItem } from "./types";

type TriggersSectionProps = {
  triggerItems: TriggerFormItem[];
  availableTriggers?: AlertTriggerDTO[];
  onAddExisting: (triggerId: number) => void;
  onAddNew: (trigger: TriggerData) => void;
  onEdit: (index: number, data: TriggerData) => void;
  onRemove: (index: number) => void;
};

const TriggersSection: React.FC<TriggersSectionProps> = ({
  triggerItems,
  availableTriggers,
  onAddExisting,
  onAddNew,
  onEdit,
  onRemove,
}) => {
  const [showNewTriggerForm, setShowNewTriggerForm] = useState(false);
  const [editingTriggerIndex, setEditingTriggerIndex] = useState<number | null>(null);

  const availableToAdd = availableTriggers?.filter(t => !triggerItems.some(item => item.existingId === t.id));

  const handleSaveEdit = (index: number, data: TriggerData) => {
    onEdit(index, data);
    setEditingTriggerIndex(null);
  };

  const handleAddNew = (trigger: TriggerData | undefined) => {
    if (!trigger) return;
    onAddNew(trigger);
    setShowNewTriggerForm(false);
  };

  return (
    <div className="border-1 border-300 border-round p-3">
      <h4 className="mt-0 mb-3">{T("alerts.triggers")}</h4>

      {/* Add existing trigger dropdown */}
      {availableToAdd && availableToAdd.length > 0 && (
        <div className="mb-3">
          <label className="block mb-1 font-semibold">
            {T("action.add")} {T("alerts.triggers")}
          </label>
          <Dropdown
            value={null}
            options={availableToAdd.map(t => ({
              label: `${t.name} (${TRIGGER_TYPE_OPTIONS.find(o => o.value === t.triggerType)?.label})`,
              value: t.id,
            }))}
            onChange={e => onAddExisting(e.value)}
            placeholder={T("action.add")}
            className="w-full"
            filter
          />
        </div>
      )}

      {/* Display all triggers as cards */}
      {triggerItems.length > 0 && (
        <div className="mb-3">
          <label className="block mb-2 font-semibold">{T("alerts.triggers")}</label>
          {triggerItems.map((item, index) => {
            const isEditing = editingTriggerIndex === index;
            const uniqueKey = item.existingId ? `existing-${item.existingId}` : `new-${index}`;

            if (isEditing && item.isExisting && item.existingTrigger) {
              return (
                <EditTriggerForm
                  key={uniqueKey}
                  trigger={item.existingTrigger}
                  onSave={data => handleSaveEdit(index, data)}
                  onCancel={() => setEditingTriggerIndex(null)}
                />
              );
            }

            return (
              <TriggerCard
                key={uniqueKey}
                item={item}
                onEdit={item.isExisting ? () => setEditingTriggerIndex(index) : undefined}
                onRemove={() => onRemove(index)}
              />
            );
          })}
        </div>
      )}

      {/* Create new trigger */}
      <div className="mb-3">
        <div className="flex justify-content-between align-items-center mb-2">
          <label className="font-semibold">
            {T("action.create")} {T("alerts.triggers")}
          </label>
          {!showNewTriggerForm && (
            <Button
              type="button"
              icon="pi pi-plus"
              label={T("action.create")}
              onClick={() => setShowNewTriggerForm(true)}
              className="p-button-sm p-button-outlined"
            />
          )}
        </div>

        {showNewTriggerForm && (
          <NewTriggerForm onSave={handleAddNew} onCancel={() => setShowNewTriggerForm(false)} />
        )}
      </div>
    </div>
  );
};

export default TriggersSection;
